import keys from '@debbie/common/dist/cache/keys.js';
import { DebtRegistrationState, RegistryType, } from '@debbie/common/dist/debt-register/index.js';
import { Site, formatNumber } from '@debbie/common/dist/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { parseOrStr } from '@debbie/common/dist/util/index.js';
import { casesEntity, ensureCaseLoaded, } from '@debbie/cortex/dist/cases/index.js';
import q from '@debbie/cortex/dist/query.js';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import ArrowDown from '../Icons/ArrowDown.js';
import { Badge, Button } from '../index.js';
import { getDescriptor } from './registries.js';
const DebtRegistrationCard = (props) => {
    const { registration, case: singleCase, setDebtorId, setRegistrationId, setActiveRegistry, } = props;
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const descriptor = getDescriptor(registration.registry);
    const openDeleteModal = () => {
        props.toggleDeleteModal();
        setDebtorId(registration.userId);
        setRegistrationId(registration.registrationId);
        setActiveRegistry(registration.registry);
    };
    const loadStatus = useMutation(async () => {
        const statusAction = descriptor.actions.status;
        if (statusAction === undefined) {
            return null;
        }
        return statusAction.call(registration.userId, singleCase.caseId, registration.registrationId);
    }, {
        onSuccess: (status) => {
            if (status !== null) {
                props.setStatus(status);
            }
        },
        onError: (error) => {
            props.setStatusModalOpen(false);
            const parsed = error instanceof Error ? parseOrStr(error.message) : error;
            errorNotification(lang, error, 'any', descriptor.errorEntity(lang), typeof parsed === 'object' &&
                parsed !== null &&
                'description' in parsed
                ? String(parsed.description)
                : undefined);
        },
    });
    const openStatusModal = () => {
        if (descriptor.actions.status === undefined) {
            return;
        }
        props.setStatusModalOpen(true);
        setActiveRegistry(registration.registry);
        setDebtorId(registration.userId);
        setRegistrationId(registration.registrationId);
        loadStatus.mutate();
    };
    const conclude = useMutation(async () => {
        const concludeAction = descriptor.actions.conclude;
        if (concludeAction === undefined) {
            return;
        }
        await concludeAction.call(registration.userId, singleCase.caseId, registration.registrationId);
        q.invalidate(keys.write.modify(casesEntity, singleCase.caseId));
        await ensureCaseLoaded(singleCase.caseId);
    }, {
        onError: (error) => {
            errorNotification(lang, error, 'any', descriptor.errorEntity(lang));
        },
    });
    // All possible per-registration actions. Each is only rendered when the
    // registry declares it (an entry in descriptor.actions) and its own state
    // predicate is met, so no branching on the specific registry is needed.
    // Labels come from the descriptor entries.
    const actions = [
        {
            action: 'delete',
            visible: DebtRegistrationState.ACTIVE === registration.state,
            onClick: openDeleteModal,
        },
        {
            action: 'moveToOpen',
            visible: DebtRegistrationState.ACTIVE === registration.state &&
                registration.registryType === RegistryType.CLOSED,
            onClick: () => {
                props.toggleMoveToOpenModal();
                setDebtorId(registration.userId);
                setRegistrationId(registration.registrationId);
                setActiveRegistry(registration.registry);
            },
        },
        {
            action: 'conclude',
            visible: DebtRegistrationState.ACTIVE === registration.state,
            onClick: () => {
                conclude.mutate();
            },
        },
        {
            action: 'error',
            visible: [
                DebtRegistrationState.ACTIVE,
                DebtRegistrationState.ERROR,
            ].includes(registration.state),
            onClick: () => {
                props.setErrorModalOpen(true);
                setRegistrationId(registration.registrationId);
                setActiveRegistry(registration.registry);
            },
        },
        {
            action: 'status',
            visible: DebtRegistrationState.ACTIVE === registration.state,
            onClick: openStatusModal,
        },
    ];
    const dropdown = site === Site.Caseworker
        ? actions.flatMap((a) => {
            const actionConfig = descriptor.actions[a.action];
            if (actionConfig == null || !a.visible) {
                return [];
            }
            return [
                {
                    value: actionConfig.label(lang),
                    onClick: a.onClick,
                    theme: 'secondary',
                },
            ];
        })
        : [];
    return (React.createElement(React.Fragment, null,
        React.createElement(DebtRegistrationCardStyled, { active: DebtRegistrationState.ACTIVE === registration.state, "data-tooltip-content": registration.error, "data-tooltip-id": `tooltip-default` },
            dropdown && !!dropdown.length && (React.createElement(Button, { loading: conclude.isLoading, size: "mini", style: {
                    float: 'right',
                    padding: '6px',
                    aspectRatio: '1 / 1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }, theme: "light", 
                // @ts-ignore
                dropdown: dropdown },
                React.createElement(ArrowDown, null))),
            React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '3px' } },
                React.createElement("img", { src: descriptor.logo, alt: descriptor.label(lang), style: { height: '18px' } }),
                React.createElement(Badge, { theme: registration.state === DebtRegistrationState.DELETED
                        ? THEME.DANGEROUS
                        : THEME.COLOR_SECONDARY, style: {
                        fontSize: '0.6em',
                        color: registration.state === DebtRegistrationState.DELETED
                            ? 'inherit'
                            : THEME.COLOR_PRIMARY,
                    } }, registration.state),
                descriptor.showRegistryType && registration.registryType && (React.createElement(Badge, { theme: registration.registryType === RegistryType.OPEN
                        ? THEME.SUCCESS
                        : THEME.NEUTRAL, style: { fontSize: '0.6em' } }, lang.debtRegistration.registry[registration.registryType]))),
            registration.createdAt && (React.createElement("div", { className: "subheader", style: { fontSize: '0.6em', marginTop: '5px' } }, moment(registration.createdAt).format(THEME.DATE_FORMAT_LONG))),
            registration && registration.claim && (React.createElement("div", { className: "price", style: {
                    fontSize: '0.85em',
                    marginTop: '5px',
                } },
                React.createElement("span", null, "DKK"),
                " ",
                formatNumber(registration.claim.amount))),
            registration &&
                registration.claim &&
                registration.claim.foundationCode && (React.createElement("div", { className: "foundation" },
                // @ts-ignore
                lang.debtRegistration.foundationCode[registration.claim.foundationCode] || registration.claim.foundationCode,
                ' ',
                moment(registration.claim.foundationDate).format(THEME.DATE_FORMAT))),
            registration.details?.migrated && (React.createElement("div", { className: "migrated" }, lang.debtRegistration.migrated)))));
};
export default DebtRegistrationCard;
const DebtRegistrationCardStyled = styled.div `
  border-radius: 4px;
  background-color: ${(props) => (props.active ? THEME.WARNING : THEME.FILL)};
  opacity: ${(props) => (props.active ? 1 : 0.6)};
  padding: 5px;
  margin-bottom: 10px;

  &:last-child {
    border-bottom: none;
  }

  & > span {
    vertical-align: middle;
  }
  .price {
    vertical-align: middle;
    display: inline-block;
    opacity: 1;
    font-weight: bold;

    span {
      margin-right: 0.3em;
    }
  }

  .subheader {
    font-size: 0.8em;
    margin-bottom: 0.6em;
  }

  .foundation {
    font-size: 0.8em;
    margin-top: 0.6em;
  }

  .migrated {
    font-size: 0.8em;
    margin-top: 0.4em;
    opacity: 0.8;
  }
`;
//# sourceMappingURL=DebtRegistrationCard.js.map